import '@fontsource/fira-sans/latin-400.css';
import '@fontsource/fira-sans/latin-500.css';
import ArrowRight16 from '@carbon/icons/es/arrow--right/16.js';
import Building32 from '@carbon/icons/es/building/32.js';
import Chat32 from '@carbon/icons/es/chat/32.js';
import ChevronDown16 from '@carbon/icons/es/chevron--down/16.js';
import Launch16 from '@carbon/icons/es/launch/16.js';
import UserMultiple32 from '@carbon/icons/es/user--multiple/32.js';
import './styles.css';

const icons = {
  'arrow-right': ArrowRight16,
  building: Building32,
  chat: Chat32,
  'chevron-down': ChevronDown16,
  launch: Launch16,
  'user-multiple': UserMultiple32,
};

const createIconNode = (descriptor) => {
  const node = document.createElementNS('http://www.w3.org/2000/svg', descriptor.elem);

  Object.entries(descriptor.attrs ?? {}).forEach(([name, value]) => {
    node.setAttribute(name, String(value));
  });

  descriptor.content?.forEach((child) => node.append(createIconNode(child)));
  return node;
};

document.querySelectorAll('[data-carbon-icon]').forEach((slot) => {
  const descriptor = icons[slot.dataset.carbonIcon];

  if (!descriptor) return;

  const icon = createIconNode(descriptor);
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  slot.append(icon);
});

document.addEventListener('click', (event) => {
  const origin = event.target instanceof Element ? event.target : null;
  const conversionLink = origin?.closest('[data-conversion-event]');

  if (!(conversionLink instanceof HTMLAnchorElement)) return;

  const detail = {
    action: conversionLink.dataset.conversionEvent,
    source: conversionLink.dataset.conversionSource,
    href: conversionLink.getAttribute('href') ?? conversionLink.href,
  };

  if (conversionLink.dataset.conversionRetailer) {
    detail.retailer = conversionLink.dataset.conversionRetailer;
  }

  window.dispatchEvent(new CustomEvent('pfm:conversion', { detail }));
});

const parallaxMedia = [...document.querySelectorAll('[data-parallax-distance]')];
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const activeParallaxMedia = new Set();
let parallaxFrame = null;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const updateParallax = () => {
  parallaxFrame = null;

  activeParallaxMedia.forEach((media) => {
    const frame = media.closest('figure') ?? media;
    const bounds = frame.getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;
    const elementCenter = bounds.top + bounds.height / 2;
    const travel = (viewportCenter - elementCenter) / ((window.innerHeight + bounds.height) / 2);
    const distance = Number.parseFloat(media.dataset.parallaxDistance) || 0;
    const offset = clamp(travel, -1, 1) * distance;

    media.style.setProperty('--pfm-parallax-y', `${offset.toFixed(2)}px`);
  });

  if (activeParallaxMedia.size > 0 && !reducedMotion.matches) {
    parallaxFrame = window.requestAnimationFrame(updateParallax);
  }
};

const requestParallaxUpdate = () => {
  if (parallaxFrame !== null || reducedMotion.matches || activeParallaxMedia.size === 0) return;
  parallaxFrame = window.requestAnimationFrame(updateParallax);
};

const parallaxObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting && !reducedMotion.matches) activeParallaxMedia.add(entry.target);
    else activeParallaxMedia.delete(entry.target);
  });

  requestParallaxUpdate();
}, { rootMargin: '35% 0px' });

parallaxMedia.forEach((media) => parallaxObserver.observe(media));

reducedMotion.addEventListener('change', () => {
  if (reducedMotion.matches) {
    activeParallaxMedia.clear();
    if (parallaxFrame !== null) window.cancelAnimationFrame(parallaxFrame);
    parallaxFrame = null;
    parallaxMedia.forEach((media) => media.style.setProperty('--pfm-parallax-y', '0px'));
    return;
  }

  parallaxMedia.forEach((media) => {
    const bounds = media.getBoundingClientRect();
    if (bounds.bottom >= -window.innerHeight * 0.35 && bounds.top <= window.innerHeight * 1.35) {
      activeParallaxMedia.add(media);
    }
  });
  requestParallaxUpdate();
});
